import { MappingSchema } from '../mapping';
import { MappingValueResolver } from '../valueResolver/valueResolverBase';

export enum ActivityEventProcessType {
  PICKING = 'PICKING',
  REPLENISHMENT = 'REPLENISHMENT',
  REASSIGN = 'REASSIGN',
  PUTAWAY = 'PUTAWAY',
  PACKING = 'PACKING',
  STACKING = 'STACKING',
  DESTACKING = 'DESTACKING',
  RECEIVING = 'RECEIVING',
  MOVE = 'MOVE',
  ALLOCATE = 'ALLOCATE',
  STAGING = 'STAGING',
  LOADING = 'LOADING',
  UNLOADING = 'UNLOADING',
  SLOTTING = 'SLOTTING',
  DISPATCH = 'DISPATCH',
  SORTING = 'SORTING',
  HANDLING = 'HANDLING',
  HANDLING_SPECIAL = 'HANDLING_SPECIAL',
  HANDLING_HAZMAT = 'HANDLING_HAZMAT',
  HANDLING_EXCEPTION = 'HANDLING_EXCEPTION',
  QUALITY_CONTROL = 'QUALITY_CONTROL',
  INVENTORY = 'INVENTORY',
  INVENTORY_COUNT = 'INVENTORY_COUNT',
  VALUE_ADD = 'VALUE_ADD',
  KITTING = 'KITTING',
  CONSOLIDATION = 'CONSOLIDATION',
  DECONSOLIDATION = 'DECONSOLIDATION',
  LABELING = 'LABELING',
  REPAIR = 'REPAIR',
  MARSHALLING = 'MARSHALLING',
  RETURNS_PROCESSING = 'RETURNS_PROCESSING',
  SHIPPING = 'SHIPPING',
  TRANSPORT = 'TRANSPORT',
  MAINTENANCE = 'MAINTENANCE',
  CROSSDOCKING = 'CROSSDOCKING',
  MANAGEMENT = 'MANAGEMENT',
  MANAGEMENT_YARD = 'MANAGEMENT_YARD',
  MANAGEMENT_DATA = 'MANAGEMENT_DATA',
  MANAGEMENT_WMS = 'MANAGEMENT_WMS',
  SYSTEM = 'SYSTEM',
  IDENTIFICATION = 'IDENTIFICATION',
  OTHER = 'OTHER',
  IGNORE = 'IGNORE',
}

export const ACTIVITY_EVENT_PROCESS_TYPE_ALL: ActivityEventProcessType[] =
  Object.values(ActivityEventProcessType).filter(
    type => type !== ActivityEventProcessType.IGNORE,
  );

export enum ActivityEventType {
  PICK = 'PICK',
  PUT = 'PUT',
  PACK = 'PACK',
  MOVE = 'MOVE',
  STACK = 'STACK',
  HOLD = 'HOLD',
  WAIT = 'WAIT',
  COUNT = 'COUNT',
  OTHER = 'OTHER',
  IGNORE = 'IGNORE',
  JOB_START = 'JOB_START',
  JOB_END = 'JOB_END',
  // SHIFT_START = 'SHIFT_START',
  // SHIFT_END = 'SHIFT_END',
  // INDIRECT_START = 'INDIRECT_START',
  // INDIRECT_END = 'INDIRECT_END',
}

export const ACTIVITY_EVENT_TYPE_ALL: ActivityEventType[] = Object.values(
  ActivityEventType,
).filter(type => type !== ActivityEventType.IGNORE);

export const eventProcessTypeValueResolver: MappingValueResolver<ActivityEventProcessType> =
  {
    picking: ActivityEventProcessType.PICKING,
    replenishment: ActivityEventProcessType.REPLENISHMENT,
    reassign: ActivityEventProcessType.REASSIGN,
    putaway: ActivityEventProcessType.PUTAWAY,
    packing: ActivityEventProcessType.PACKING,
    receiving: ActivityEventProcessType.RECEIVING,
    quality_control: ActivityEventProcessType.QUALITY_CONTROL,
    inventory_count: ActivityEventProcessType.INVENTORY_COUNT,
    returns_processing: ActivityEventProcessType.RETURNS_PROCESSING,
    shipping: ActivityEventProcessType.SHIPPING,
    maintenance: ActivityEventProcessType.MAINTENANCE,
    cross_docking: ActivityEventProcessType.CROSSDOCKING,
    yard_management: ActivityEventProcessType.MANAGEMENT_YARD,
    data_management: ActivityEventProcessType.MANAGEMENT_DATA,
    management: ActivityEventProcessType.MANAGEMENT,
    system: ActivityEventProcessType.SYSTEM,
    other: ActivityEventProcessType.OTHER,
    ignore: ActivityEventProcessType.IGNORE,
  };

export const eventTypeValueResolver: MappingValueResolver<ActivityEventType> = {
  pick: ActivityEventType.PICK,
  put: ActivityEventType.PUT,
  pack: ActivityEventType.PACK,
  count: ActivityEventType.COUNT,
  move: ActivityEventType.MOVE,
  other: ActivityEventType.OTHER,
  ignore: ActivityEventType.IGNORE,
  job_start: ActivityEventType.JOB_START,
  job_end: ActivityEventType.JOB_END,
  // shift_start: ActivityEventType.SHIFT_START,
  // shift_end: ActivityEventType.SHIFT_END,
  // indirect_start: ActivityEventType.INDIRECT_START,
  // indirect_end: ActivityEventType.INDIRECT_END,
};

export class ActivityFeedEvent {
  eventId: string;
  eventProcessType: ActivityEventProcessType;
  eventType: ActivityEventType;

  //what
  consignee?: string;
  sku?: string;
  uom?: string;
  quantity?: number;
  scanCode?: string;

  //previous location
  warehouseAreaSource?: string;
  locationIdSource?: string;

  //current location
  warehouseArea?: string;
  locationId: string;

  //who
  agentType: string; //mheType
  agentId?: string; //mheId
  agentUser?: string;
  agentEnergy?: number;
  agentSignalStrength?: number;

  //when
  eventStartTime?: Date;
  eventEndTime: Date;

  // picklist/job meta
  jobId?: string;
  jobLine?: number;
  jobGroupId?: string;
}

export const ACTIVITY_FEED_MAPPING_SCHEMA: MappingSchema<ActivityFeedEvent> = {
  title: `Activity Feed Event`,
  description: `
    A collection of events that occur in the warehouse, usually generated by the warehouse management system (WMS), or IoT devices like barcode scanners, RFID readers, etc.
    Used to as base to analyze the warehouse operations and identify inefficiencies, bottlenecks, and areas for improvement.
    Acts as historical data for the warehouse operations and can be used to help AI models to predict future events and optimize the warehouse operations.
    can be further enriched with analytical data, metrics, and other data to provide a more comprehensive view of the warehouse operations.
    `,
  partitionBy: [{ key: 'eventEndTime', transform: 'day' }],
  fields: [
    {
      name: 'eventId',
      type: 'string',
      unique: true,
      title: `Event ID`,
      description: `
        Unique identifier for the event that occurs in the warehouse,
        ensuring that each activity can be uniquely traced.
        (Alias Examples: Event ID, Transaction ID, Log ID, ID)
        `,
    },
    {
      name: 'eventProcessType',
      type: { enumValues: ACTIVITY_EVENT_PROCESS_TYPE_ALL },
      defaultValueResolver: eventProcessTypeValueResolver,
      title: 'Process type',
      description: `
        Process Type category, descibes different types of activities that occurs in the warehouse such as Picking, Packing, Replenishment, Putaway etc.
        Used as a filter to analyze different types of activities in the warehouse.
        (Alias Examples: Process Type, Process, Type, Category)

        refer to: ActivityEventProcessTypeDetails for more details for each process type
      `,
      effectiveEnum: true,
    },
    {
      name: 'eventType',
      type: { enumValues: ACTIVITY_EVENT_TYPE_ALL },
      defaultValueResolver: eventTypeValueResolver,
      title: 'Event type',
      description: `
      Describes the specific action or operation that occurred during the event, such as Pick, Put, Move, etc.
      Works as a companion to the eventProcessType to provide a more detailed description of the action that was done event.      refer to useActivityEventTypeDetails for more details for each event type
      `,
      effectiveEnum: true,
    },

    {
      name: 'consignee',
      type: 'string',
      optional: true,
      title: `Client (Consignee)`,
      description: `
      Client (consignee) — Name of your company (can be generated) or your client (For 3PLs)
      Act as unique ID for the SKU.
      (Alias Examples: Client, Consignee, Company, Customer)
      `,
      effectiveEnum: true,
    },
    {
      name: 'sku',
      type: 'string',
      optional: true,
      title: `Item`,
      description: `
      The Item / Product (SKU) code for the item involved in the activity.
      (Item code, SKU, Product ID, Part Number, Article etc.)`,
    },
    {
      name: 'uom',
      type: 'string',
      optional: true,
      title: 'UOM',
      description:
        'Unit of measure (UOM), such as individual pieces, cases, layers or pallets.',
      effectiveEnum: true,
    },
    {
      name: 'quantity',
      type: 'integer',
      optional: true,
      title: 'UOM quantity',
      description:
        'The quantity of the item (UOM) handled, expressed in the unit of measure specified.',
    },
    {
      name: 'scanCode',
      type: 'string',
      optional: true,
      title: 'Scan code',
      description:
        'The barcode (EAN/UPC) or QR codes associated with the item.',
    },
    {
      name: 'warehouseAreaSource',
      type: 'string',
      optional: true,
      title: 'Area Source',
      description:
        'Warehouse Area (Source), providing additional location data for the event.',
      effectiveEnum: true,
      excludeFromAiPrompt: true,
    },
    {
      name: 'locationIdSource',
      type: 'string',
      optional: true,
      title: 'Location ID Source',
      description:
        'Location ID (Source/From), typically in the format AA010101.',
      excludeFromAiPrompt: true,
    },

    {
      name: 'warehouseArea',
      type: 'string',
      optional: true,
      title: 'Area',
      description:
        'Warehouse Area (Dest.), providing additional location zoning data for the event.',
      effectiveEnum: true,
    },
    {
      name: 'locationId',
      type: 'string',
      title: 'Location ID',
      description:
        'Location ID/Name, a position that the event happened, typically in the format AA010101',
    },
    {
      name: 'agentType',
      type: 'string',
      title: 'Agent type',
      description: `
      Classifies the type of MHE, Device or Team involved in the event, categorizing the resources used for different operations.
      Is used as a filter to analyze different types of agents or processes in the warehouse.
      Such Case Pick, Pallet Pick, or Forklifts, Order Picker, Trolly etc.
      (Alias Examples: Agent Type, Agent, Team, Device Type, Device, Team, MHE Type, MHE, Forklift, Robot, etc.)
      `,
      effectiveEnum: true,
    },
    {
      name: 'agentId',
      type: 'string',
      optional: true,
      title: 'Agent ID',
      description: `
      The ID for the specific equipment, device, or employee performing the event.
      Used for linking the event to the specific agent (equipment, device, or employee) that performed the event.
      and then analyse the performance of the agent.
      (Alias Examples: Agent ID, Forklift ID, Device ID, User ID, Employee ID, Robot ID, etc.)
        `,
    },
    {
      name: 'agentUser',
      type: 'string',
      optional: true,
      title: `Agent User`,
      description: `
      The username of the individual or the (SN) identifier of the Employee 
      or automated robot or device doing the event,
      providing a direct or obfuscated link to the employee or equipment name or title.
      Agent User and agent Id sometimes have the same value.
      (Alias Examples: User Id, Agent User, Agent Name, Agent Username, Agent Email, Agent ID, Employee ID, Employee Name, Employee Username, Employee Email)
      `,
    },
    {
      name: 'agentEnergy',
      type: 'number',
      optional: true,
      title: 'Agent Energy',
      description: `The energy level or battery percentage of the device like the Scanner or MHE (Material Handling Equipment). 
      Should not be used for calculations process efficiency. 
      Used for analysing device energy consumption on the warehouse floor.
      (Alias Examples: Battery Level, Battery Percentage, Battery, Energy, Energy Level)
      `,
    },
    {
      name: 'agentSignalStrength',
      type: 'number',
      optional: true,
      title: `Signal strength`,
      description: `
      The strength of the WiFi/Modem signal for the device involved in the event, which can impact the reliability of event logging and real-time data communication
      Not used for logistics process efficiency.
      Used for analysing signal strength and device reliability on the warehouse floor.
      (Alias Examples: Signal Strength, WiFi Signal, Modem Signal, Signal, RSSI)
      `,
    },
    {
      name: 'eventStartTime',
      type: 'localDateTime',
      measureType: 'local-date-time',
      optional: true,
      title: 'Event start timestamp',
      description:
        'The exact time when the event started, optional value for more detailed tracking if exists.',
      excludeFromAiPrompt: true,
    },
    {
      name: 'eventEndTime',
      type: 'localDateTime',
      measureType: 'local-date-time',
      title: 'Event end timestamp',
      description: `
      Primary timestamp denoting when the event concluded.
      Vital for time tracking and efficiency analysis.
      (Alias Examples: Event End, End Time, End Timestamp, Finished At, Completed At)
      `,
    },

    {
      name: 'jobId',
      type: 'string',
      optional: false,
      title: 'Job ID',
      description: `
      Groups events under a common job identifier (e.g. Picklist ID, Work Assignment).
      Considered the primary key for linking related operations.
      (Alias Examples: Job ID, Job, Task ID, Mission ID, Order ID, Work Assignment ID)
      `,
    },
    {
      name: 'jobLine',
      type: 'integer',
      optional: true,
      title: 'Job group ID',
      description: `
      Links the event to a broader group of related tasks, such as client PO Orders, waves or batches, facilitating large-scale operation management
      (Alias Examples: Job Group ID, Job Group, Task Group ID, Mission Group ID, Order Group ID, Work Assignment Group ID)
      `,
    },
    {
      name: 'jobGroupId',
      type: 'string',
      optional: true,
      title: 'Job group ID',
      description: `
      Links the event to broader operational groups (e.g. client PO Orders, waves, or batches).
      (Alias Examples: Job Group ID, Job Group, Task Group ID, Mission Group ID, Order Group ID, Work Assignment Group ID)
      `,
    },
  ],
  includeRawData: true,
  primaryKey: ['eventId'],
  appendable: true,
};
